import { Storage } from 'aws-amplify';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

interface CachedObject {
  url: string;
  timestamp: number;
}

interface S3ObjectContextType {
  getS3ObjectUrl: (s3Key: string) => Promise<string | undefined>;
  s3ObjectCache: Record<string, string>; // Keep providing simple string map for UI components
  updateS3ObjectCache: (s3Key: string, url: string) => void;
  invalidateS3ObjectCache: (s3Key: string) => void;
}

const DEFAULT_EXPIRY = 3600; // 1 hour
const REFRESH_THRESHOLD = 3000; // 50 minutes (fetch fresh URL 10 mins before expiry)

const S3ObjectContext = createContext<S3ObjectContextType | undefined>(undefined);

export const S3ObjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Record<string, CachedObject>>({});
  const fetchPromises = useRef<Record<string, Promise<string | undefined> | undefined>>({});

  // Derive a simple string map for the context consumer's convenience
  const s3ObjectCache = React.useMemo(() => {
    const simpleMap: Record<string, string> = {};
    Object.entries(cache).forEach(([key, val]) => {
      simpleMap[key] = val.url;
    });
    return simpleMap;
  }, [cache]);

  const updateS3ObjectCache = useCallback((s3Key: string, url: string) => {
    setCache(prev => ({
      ...prev,
      [s3Key]: { url, timestamp: Date.now() }
    }));
  }, []);

  const invalidateS3ObjectCache = useCallback((s3Key: string) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[s3Key];
      return newCache;
    });
  }, []);

  const getS3ObjectUrl = useCallback(async (s3Key: string) => {
    if (!s3Key || s3Key === 'NULL') return undefined;

    const now = Date.now();
    const cachedItem = cache[s3Key];

    // Check if we have a valid cached URL
    if (cachedItem && (now - cachedItem.timestamp) < (REFRESH_THRESHOLD * 1000)) {
      return cachedItem.url;
    }

    // Check if a fetch is already in progress for this key
    const existingPromise = fetchPromises.current[s3Key];
    if (existingPromise) {
      return existingPromise;
    }

    // Start a new fetch
    const fetchPromise = (async () => {
      try {
        const url = await Storage.get(s3Key, {
          expires: DEFAULT_EXPIRY,
          cacheControl: 'no-cache', // Ensure we get fresh content from S3
          contentDisposition: 'inline'
        }) as string;
        setCache(prev => ({
          ...prev,
          [s3Key]: { url, timestamp: Date.now() }
        }));
        return url;
      } catch (e) {
        console.error('Error fetching S3 object', e);
        return undefined;
      } finally {
        // Clean up promise after it finishes
        delete fetchPromises.current[s3Key];
      }
    })();

    fetchPromises.current[s3Key] = fetchPromise;
    return fetchPromise;
  }, [cache]);

  return (
    <S3ObjectContext.Provider value={{ getS3ObjectUrl, s3ObjectCache, updateS3ObjectCache, invalidateS3ObjectCache }}>
      {children}
    </S3ObjectContext.Provider>
  );
};

export const useS3Object = () => {
  const context = useContext(S3ObjectContext);
  if (!context) {
    throw new Error('useS3Object must be used within an S3ObjectProvider');
  }
  return context;
};
